import * as React from 'react';
import { Icon } from 'office-ui-fabric-react';
import { Field, getOption } from '../../entities/Metadata';
import { DisplayField } from '../common/DisplayField';
import { getDisplayTypeDescriptor } from '../field/FieldPanel';
import { getIconName } from '../field/FieldOption';
import { EntityType } from '../../entities/common';

// Helper function to convert string entity type to EntityType enum
const getEntityTypeEnum = (entityTypeString: string): EntityType | undefined => {
    // Convert to lowercase and find matching enum value
    const normalizedType = entityTypeString.toLowerCase();
    return Object.values(EntityType).find(enumValue => enumValue === normalizedType);
};

interface FieldChangesProps {
    attributeDeltas: { [key: string]: any };
    entityType: string;
    description?: string;
    message?: string;
}

export const FieldChanges: React.FC<FieldChangesProps> = ({
    attributeDeltas,
    entityType,
    description,
    message
}) => {
    const hasChanges = Object.keys(attributeDeltas).length > 0;
    const entityTypeEnum = getEntityTypeEnum(entityType);

    if (!hasChanges) {
        return null;
    }

    return (
        <div className="changes-container">
            {Object.entries(attributeDeltas).map(([key, delta]: [string, any]) => {
                const oldValue = delta.oldValue;
                const newValue = delta.newValue;
                const changeDescription = delta.changeDescription;

                // Convert FieldDisplayInfo to Field for display
                const fieldInfo = delta.fieldInfo;
                const fieldMock = {
                    id: fieldInfo?.name || key,
                    name: fieldInfo?.name || key,
                    label: fieldInfo?.label || fieldInfo?.name || key,
                    type: fieldInfo?.type,
                    settings: fieldInfo?.displaySettings
                } as any as Field;

                // Get icon from DisplayType provided by server
                const iconName = fieldInfo?.displayType !== undefined
                    ? getDisplayTypeDescriptor(fieldInfo.displayType)?.iconName
                    : getIconName(fieldMock); // fallback

                //remove options, className, editControl from fieldMock.settings to fix displayField
                if(fieldMock.settings?.options && !getOption(fieldMock, newValue?.toString()) && !getOption(fieldMock, oldValue?.toString())) {
                    delete fieldMock.settings?.options;
                    delete fieldMock.settings?.className;
                    delete fieldMock.settings?.editControl;
                }

                return (
                    <div key={key}
                        title={changeDescription}
                        className="change-item">
                        <div className="change-field">
                            <Icon iconName={iconName} />
                            <span className="field-label">{fieldMock.label}:</span>
                        </div>

                        <div className="change-values">
                            <div className="old-value">
                                {/* null as well as undefined: a Created entry carries no previous state at all. */}
                                {oldValue !== undefined && oldValue !== null && (!Array.isArray(oldValue) || oldValue.length > 0)
                                    ? <DisplayField entityType={entityTypeEnum} field={fieldMock} entity={{ attributes: { [fieldMock.name]: oldValue } as any }} />
                                    : <span>Empty</span>
                                }
                            </div>

                            <div className="arrow-icon">
                                <Icon iconName="SortUp" />
                            </div>

                            <div className="new-value">
                                {newValue !== undefined && newValue !== null && (!Array.isArray(newValue) || newValue.length > 0)
                                    ? <DisplayField entityType={entityTypeEnum} field={fieldMock} entity={{ attributes: { [fieldMock.name]: newValue } as any }} />
                                    : <span>Empty</span>
                                }
                            </div>
                        </div>
                    </div>
                );
            })}

            {!hasChanges && (
                <div className="no-changes-message">
                    {description ?? message ?? ''}
                </div>
            )}
        </div>
    );
};
